import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import axios from "api/axios";
import { useDispatch, useSelector } from "react-redux";
import { setUser, setTimezone } from "state";
import { dialogSelectMenuProps } from "utils/selectMenuProps";
import {
  TIMEZONE_OPTIONS,
  APP_DATA_TIMEZONE,
  getTimezoneAbbr,
  getTimezoneLabel,
} from "utils/datetime";

const SettingsDialog = ({ open, onClose, user }) => {
  const dispatch = useDispatch();
  const timezone = useSelector((s) => s.global.timezone);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (user?.avatar) {
      setAvatarPreview(user.avatar);
      return;
    }
    let cancelled = false;
    axios
      .get("/auth/avatar")
      .then((res) => {
        if (!cancelled) setAvatarPreview(res.data?.avatar || null);
      })
      .catch(() => {
        if (!cancelled) setAvatarPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user?._id, user?.avatar]);

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Avatar image must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError("");
    }
  };

  const handleUpdateAvatar = async () => {
    if (!avatarFile) {
      setError("Please select an avatar image");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const response = await axios.put("/auth/update-avatar", {
        avatar: avatarPreview,
      });

      if (response.data.user) {
        dispatch(setUser({ ...response.data.user, hasAvatar: true }));
      }

      setSuccess("Avatar updated successfully!");
      setAvatarFile(null);
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update avatar");
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = (tz) => {
    dispatch(setTimezone(tz));
  };

  const handleClose = () => {
    setAvatarFile(null);
    setAvatarPreview(user?.avatar || null);
    setError("");
    setSuccess("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Display timezone
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Timezone</InputLabel>
            <Select
              label="Timezone"
              value={timezone}
              onChange={(e) => handleTimezoneChange(e.target.value)}
              MenuProps={dialogSelectMenuProps}
            >
              {TIMEZONE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Currently showing {getTimezoneLabel(timezone)} ({getTimezoneAbbr(timezone)}).
            Schedules and daily logs use Central Time ({getTimezoneAbbr(APP_DATA_TIMEZONE)}) by default.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            Profile avatar
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={avatarPreview} sx={{ width: 80, height: 80 }} />
            <Box>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload">
                <IconButton color="primary" component="span">
                  <PhotoCamera />
                </IconButton>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Click to upload (Max 5MB)
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button onClick={handleUpdateAvatar} variant="contained" disabled={loading || !avatarFile}>
          {loading ? "Updating..." : "Save avatar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
